import { useMemo, memo } from 'react';
import { BusinessUnderstanding } from '@Pages/GeoMMM/Results/BusinessUnderstanding';
import { InvestmentPerformance } from '@Pages/GeoMMM/Results/InvestmentPerformance';
import { NextMediaDollar } from '@Pages/GeoMMM/Results/NextMediaDollar';
import { ModelInsights } from '@Pages/GeoMMM/Results/ModelInsights';
import { GeographicAnalysis } from '@Pages/GeoMMM/Results/GeographicAnalysis';
import { FullPageSpinner } from '@Components/Spinner/Spinner';

interface ResultsProps {
  groupBy: string;
  outcomeVariable?: 'revenue' | 'kpiVolume';
  isLoading: boolean;
  summaryMetricsRows: any[];
  mapeByGeoRows: any[];
  fitRows: any[];
  mediaSpendRows: any[];
  channelBreakoutMap: Record<string, any>;
  geoNames: Record<string, string>;
  attributionOverTimeRows: any[];
  roiTableRows: any[];
  responseCurvesRows: any[];
  adstockRows: any[];
  attributionByGeoRows: any[];
  responseCurveDivisor?: number;
  lastRefreshDate?: string;
}

export const Results = memo(
  ({
    groupBy,
    outcomeVariable = 'revenue',
    isLoading,
    summaryMetricsRows,
    mapeByGeoRows,
    fitRows,
    mediaSpendRows,
    channelBreakoutMap,
    geoNames,
    attributionOverTimeRows,
    roiTableRows,
    responseCurvesRows,
    adstockRows,
    attributionByGeoRows,
    responseCurveDivisor = 1,
    lastRefreshDate = ''
  }: ResultsProps) => {
    const maxDimension: string = useMemo(() => {
      let maxDimensionIndex = 0;
      for (const breakoutName in channelBreakoutMap) {
        if (!breakoutName) {
          continue;
        }
        const breakoutDimensions = channelBreakoutMap[breakoutName];
        for (const dimensionKey in breakoutDimensions) {
          if (!dimensionKey || dimensionKey === 'channel') {
            continue;
          }
          if (dimensionKey === 'brand_nonbrand' && breakoutDimensions[dimensionKey]) {
            maxDimensionIndex = Math.max(maxDimensionIndex, 3);
            break;
          }
          if (dimensionKey === 'tactic' && breakoutDimensions[dimensionKey]) {
            maxDimensionIndex = Math.max(maxDimensionIndex, 2);
            continue;
          }
          if (dimensionKey === 'platform' && breakoutDimensions[dimensionKey]) {
            maxDimensionIndex = Math.max(maxDimensionIndex, 1);
            continue;
          }
        }
      }

      switch (maxDimensionIndex) {
        case 3:
          return 'Brand/Non-Brand';
        case 2:
          return 'Tactic';
        case 1:
          return 'Platform';
        default:
          return 'Channel';
      }
    }, [channelBreakoutMap]);

    return !isLoading ? (
      <div className='geo-mmm-results-sections' data-testid='geo-mmm-results'>
        <BusinessUnderstanding
          groupBy={groupBy}
          outcomeVariable={outcomeVariable}
          summaryMetricsRows={summaryMetricsRows}
          mapeByGeoRows={mapeByGeoRows}
          fitRows={fitRows}
          mediaSpendRows={mediaSpendRows}
          channelBreakoutMap={channelBreakoutMap}
          geoNames={geoNames}
          lastRefreshDate={lastRefreshDate}
        />
        <InvestmentPerformance
          groupBy={groupBy}
          outcomeVariable={outcomeVariable}
          attributionOverTimeRows={attributionOverTimeRows}
          channelBreakoutMap={channelBreakoutMap}
        />
        <NextMediaDollar
          roiTableRows={roiTableRows}
          channelBreakoutMap={channelBreakoutMap}
          outcomeVariable={outcomeVariable}
          maxDimension={maxDimension}
        />
        <ModelInsights
          outcomeVariable={outcomeVariable}
          responseCurvesRows={responseCurvesRows}
          adstockRows={adstockRows}
          channelBreakoutMap={channelBreakoutMap}
          maxDimension={maxDimension}
          responseCurveDivisor={responseCurveDivisor}
        />
        <GeographicAnalysis
          groupBy={groupBy}
          outcomeVariable={outcomeVariable}
          attributionByGeoRows={attributionByGeoRows}
          geoNames={geoNames}
          channelBreakoutMap={channelBreakoutMap}
        />
      </div>
    ) : (
      <FullPageSpinner />
    );
  }
);
